<script setup lang="ts">
import { computed } from 'vue'

import type { ReportDatePickerHydration } from '../../models/datePicker.types'

import {
  DateRangeControl,
  getWorkspaceDateRangeDefault,
  valueFromSelection,
  type DateRangeSelection,
} from '@/shared/date-range'
import { useI18n } from '@/shared/i18n'
import { parseLooseDate } from '@/shared/lib/date/parse'
import { toApiDate } from '@/shared/lib/date/toApiDate'
import {
  resolvePreset,
  resolveCompareRange,
  type CompareMode,
  type DatePreset,
  type DatePickerProps,
  type DatePresetId,
  type DateRangeValue,
  type MonthLabels,
  type WeekdayLabels,
} from '@/shared/ui'

// The reports-owned date seam (v3 `o-date-picker` + `dateStore.getSelectedDatePost`): dates are
// shared keyed state serialized to the `&date_start=…` query fragment DataVisible's request layer
// understands. The query fragment remains report vocabulary; persistence/bounds/fan-out do not.
//
// Defaults resolve in v3's order (`OrphexDatePicker.vue:398-402, :422`): the page CONFIG wins,
// then the workspace's `ws_selection_default_date` attribute, then the hardcoded fallback
// (ORP-19877). `defaultPreset`/`compareDefault` therefore have NO `withDefaults` value — giving
// them one collapses "config said nothing" into "config said 'Last 28 Days'" and the attribute
// could never win, which is exactly how the setting came to be a no-op.

const props = withDefaults(
  defineProps<{
    /** Config default preset id (v3 `defaultDateValue`/`defaultRange`, same literal ids). */
    defaultPreset?: DatePresetId | undefined
    /** Compare toggle default (v3 `compareDefault`). Unset ⇒ fall through to the workspace value. */
    compareDefault?: boolean | undefined
    /** Hide compare entirely (v3 `hiddenCompare`). */
    hiddenCompare?: boolean | undefined
    selectablePresets?: DatePresetId[] | null | undefined
    id?: string | undefined
    linkedIds?: string[] | undefined
    min?: Date | undefined
    max?: Date | undefined
    /** Caller-owned persisted widget dates; when present they outrank transient keyed state. */
    hydration?: ReportDatePickerHydration | undefined
  }>(),
  // `compareDefault: undefined` is load-bearing, not noise: a `boolean` prop with no declared
  // default gets Vue's Boolean casting, so an absent one arrives as `false` — which would read as
  // "config turned compare off" and swallow the workspace value. Declaring the default keeps
  // absence as `undefined` so `??` can fall through.
  {
    compareDefault: undefined,
    hiddenCompare: false,
    selectablePresets: null,
    id: 'reports:default',
    linkedIds: () => [],
  },
)
const emit = defineEmits<{ change: [postData: string] }>()
const { t } = useI18n()

/** 22 writes `ws_selection_default_date` as `{compareType, compareEnabled, activePreset}`, but 07's
 *  `getAttribute` hands back the untyped ROW, so every field is narrowed here rather than trusted.
 *  Ids are validated against this component's own preset/compare lists — a stale or hand-edited
 *  value falls through to the next step of the chain instead of reaching `resolvePreset`. */
function initialValue(): DateRangeValue {
  const workspace = getWorkspaceDateRangeDefault()
  const presetId =
    props.hydration?.presetId ?? props.defaultPreset ?? workspace.activePreset ?? 'Last 28 Days'
  const compareWanted =
    props.hydration?.compareEnabled ?? props.compareDefault ?? workspace.compareEnabled ?? true
  const range = resolvePreset(presetId, new Date(), null)
  const hydratedStart = parseHydratedDate(props.hydration?.start)
  const hydratedEnd = parseHydratedDate(props.hydration?.end)
  const start = hydratedStart && hydratedEnd ? hydratedStart : (range?.start ?? null)
  const end = hydratedStart && hydratedEnd ? hydratedEnd : (range?.end ?? null)
  const compareEnabled = compareWanted && !props.hiddenCompare && start !== null
  const compareType = props.hydration?.compareType ?? workspace.compareType ?? 'Previous Period'
  const hydratedCompareStart = parseHydratedDate(props.hydration?.compareStart)
  const hydratedCompareEnd = parseHydratedDate(props.hydration?.compareEnd)
  const compare =
    hydratedCompareStart && hydratedCompareEnd
      ? { start: hydratedCompareStart, end: hydratedCompareEnd }
      : resolveCompareRange(compareType, start, end, { start: null, end: null }, compareEnabled)
  return {
    start,
    end,
    presetId,
    compareEnabled,
    compareType,
    compareStart: compare.start,
    compareEnd: compare.end,
  }
}

function parseHydratedDate(value: string | null | undefined): Date | null {
  if (!value) return null
  const parsed = parseLooseDate(value)
  return Number.isNaN(parsed.getTime()) ? null : parsed
}

function serialize(value: DateRangeValue): string {
  const params = new URLSearchParams()
  if (value.start) params.set('date_start', toApiDate(value.start))
  if (value.end) params.set('date_end', toApiDate(value.end))
  if (!props.hiddenCompare && value.compareEnabled && value.compareStart) {
    params.set('compare_date_start', toApiDate(value.compareStart))
  }
  if (!props.hiddenCompare && value.compareEnabled && value.compareEnd) {
    params.set('compare_date_end', toApiDate(value.compareEnd))
  }
  return `&${params.toString()}`
}

const presets = computed<DatePreset[]>(() => [
  { id: 'Yesterday', label: t('reports.datePicker.presets.yesterday') },
  { id: 'This Week', label: t('reports.datePicker.presets.thisWeek') },
  { id: 'Last Week', label: t('reports.datePicker.presets.lastWeek') },
  { id: 'This Month', label: t('reports.datePicker.presets.thisMonth') },
  { id: 'Last Month', label: t('reports.datePicker.presets.lastMonth') },
  { id: 'This Year', label: t('reports.datePicker.presets.thisYear') },
  { id: 'Last Year', label: t('reports.datePicker.presets.lastYear') },
  { id: 'Last 7 Days', label: t('reports.datePicker.presets.last7Days') },
  { id: 'Last 14 Days', label: t('reports.datePicker.presets.last14Days') },
  { id: 'Last 28 Days', label: t('reports.datePicker.presets.last28Days') },
  { id: 'Last 30 Days', label: t('reports.datePicker.presets.last30Days') },
  { id: 'Last 60 Days', label: t('reports.datePicker.presets.last60Days') },
  { id: 'Last 90 Days', label: t('reports.datePicker.presets.last90Days') },
  { id: 'Maximum', label: t('reports.datePicker.presets.maximum') },
  { id: 'Custom', label: t('reports.datePicker.presets.custom') },
])
const compareModes = computed<CompareMode[]>(() => [
  { id: 'Previous Period', label: t('reports.datePicker.compare.previousPeriod') },
  { id: 'Previous Week', label: t('reports.datePicker.compare.previousWeek') },
  { id: 'Previous Month', label: t('reports.datePicker.compare.previousMonth') },
  { id: 'Previous Year', label: t('reports.datePicker.compare.previousYear') },
])

// Declared after the two lists above on purpose: `initialValue()` validates the workspace
// attribute's ids against them, so calling it any earlier hits their temporal dead zone.
const weekdayLabels = computed<WeekdayLabels>(() => [
  t('reports.datePicker.weekdays.mon'),
  t('reports.datePicker.weekdays.tue'),
  t('reports.datePicker.weekdays.wed'),
  t('reports.datePicker.weekdays.thu'),
  t('reports.datePicker.weekdays.fri'),
  t('reports.datePicker.weekdays.sat'),
  t('reports.datePicker.weekdays.sun'),
])
const monthLabels = computed<MonthLabels>(() => [
  t('reports.datePicker.months.jan'),
  t('reports.datePicker.months.feb'),
  t('reports.datePicker.months.mar'),
  t('reports.datePicker.months.apr'),
  t('reports.datePicker.months.may'),
  t('reports.datePicker.months.jun'),
  t('reports.datePicker.months.jul'),
  t('reports.datePicker.months.aug'),
  t('reports.datePicker.months.sep'),
  t('reports.datePicker.months.oct'),
  t('reports.datePicker.months.nov'),
  t('reports.datePicker.months.dec'),
])

const picker = computed<Omit<DatePickerProps, 'min' | 'max'>>(() => ({
  compact: true,
  presets: presets.value,
  selectablePresets: props.selectablePresets,
  hideCompare: props.hiddenCompare,
  compareModes: compareModes.value,
  weekdayLabels: weekdayLabels.value,
  monthLabels: monthLabels.value,
  triggerLabel: t('reports.datePicker.trigger'),
  emptyLabel: t('reports.datePicker.empty'),
  rangeSeparator: t('reports.datePicker.rangeSeparator'),
  compareSeparator: t('reports.datePicker.compareSeparator'),
  compareToggleLabel: t('reports.datePicker.compareToggle'),
  startInputLabel: t('reports.datePicker.start'),
  endInputLabel: t('reports.datePicker.end'),
  compareStartInputLabel: t('reports.datePicker.compareStart'),
  compareEndInputLabel: t('reports.datePicker.compareEnd'),
  startPlaceholder: t('reports.datePicker.start'),
  endPlaceholder: t('reports.datePicker.end'),
  compareStartPlaceholder: t('reports.datePicker.compareStart'),
  compareEndPlaceholder: t('reports.datePicker.compareEnd'),
  resetLabel: t('reports.datePicker.reset'),
  applyLabel: t('reports.datePicker.apply'),
}))

const resetKey = computed(() =>
  JSON.stringify([props.defaultPreset, props.compareDefault, props.hiddenCompare, props.hydration]),
)

function change(selection: DateRangeSelection): void {
  emit('change', serialize(valueFromSelection(selection)))
}

// Config defaults change when the active page tab changes (v3 remount-keyed the picker).
//
// The workspace attribute is deliberately NOT a trigger here, so changing the setting applies on
// the next mount/navigation rather than instantly (ORP-19877's open question). That is v3's
// behaviour — its `keyAttr` is reactive, but only the mount-time `initDates()` path reads it, so a
// save never moved the range under an open report — and it keeps a settings write in another tab
// from silently re-running the current page's query mid-analysis.
</script>

<template>
  <DateRangeControl
    :id="id"
    :linked-ids="linkedIds"
    :default-value="initialValue()"
    :prefer-default-value="hydration !== undefined"
    :reset-key="resetKey"
    :picker="{ ...picker, ...(min ? { min } : {}), ...(max ? { max } : {}) }"
    @change="change"
  />
</template>
